import { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, Users, Loader2, Search, X, ChevronRight, Save, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";
import { Switch } from "@/components/ui/switch";

function StatMini({ Icon, label, value, accent }) {
    return (
        <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <div className="text-[11.5px] text-muted-foreground">{label}</div>
                    <div className="text-[20px] font-bold leading-tight tracking-tight">{value}</div>
                </div>
            </div>
        </div>
    );
}

export default function RolesList() {
    const canView = useCan("roles.view");
    const canEdit = useCan("roles.edit");

    const [roles, setRoles] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [activeRole, setActiveRole] = useState(null);
    const [permSet, setPermSet] = useState(new Set());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rolesRes, permsRes] = await Promise.all([
                api.get("/roles"),
                api.get("/permissions/grouped"),
            ]);
            setRoles(rolesRes.data.items || []);
            setGroups(permsRes.data.groups || []);
            if (!activeRole && rolesRes.data.items?.length) {
                setActiveRole(rolesRes.data.items[0]);
                setPermSet(new Set(rolesRes.data.items[0].permissions || []));
            } else if (activeRole) {
                // refresh active
                const fresh = rolesRes.data.items.find((r) => r.name === activeRole.name);
                if (fresh) {
                    setActiveRole(fresh);
                    setPermSet(new Set(fresh.permissions || []));
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Yüklenemedi.");
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { if (canView) load(); }, [load, canView]);

    const selectRole = (r) => {
        setActiveRole(r);
        setPermSet(new Set(r.permissions || []));
    };

    const filtered = useMemo(() => {
        if (!search) return roles;
        const q = search.toLowerCase();
        return roles.filter((r) =>
            (r.display_name || "").toLowerCase().includes(q)
            || (r.name || "").toLowerCase().includes(q)
            || (r.description || "").toLowerCase().includes(q)
        );
    }, [roles, search]);

    const togglePerm = (code) => {
        const s = new Set(permSet);
        s.has(code) ? s.delete(code) : s.add(code);
        setPermSet(s);
    };

    const toggleGroup = (groupPerms, allSelected) => {
        const s = new Set(permSet);
        groupPerms.forEach((p) => allSelected ? s.delete(p.code) : s.add(p.code));
        setPermSet(s);
    };

    const saveRole = async () => {
        if (!activeRole) return;
        setSaving(true);
        try {
            await api.put(`/roles/${activeRole.name}`, { permissions: Array.from(permSet) });
            toast.success("Rol yetkileri güncellendi.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kaydedilemedi.");
        } finally {
            setSaving(false);
        }
    };

    if (!canView) return <Forbidden />;

    const totalPerms = groups.reduce((sum, g) => sum + g.permissions.length, 0);
    const systemRoles = roles.filter((r) => r.is_system).length;

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight">{tr.roles.title}</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">{tr.roles.subtitle}</p>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatMini Icon={ShieldCheck} label={tr.roles.totalRoles} value={roles.length} accent="bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]" />
                <StatMini Icon={ShieldCheck} label={tr.roles.systemRoles} value={systemRoles} accent="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]" />
                <StatMini Icon={Users} label="Toplam Yetki" value={totalPerms} accent="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" />
            </div>

            {loading ? (
                <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> {tr.common.loading}</div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
                    {/* Roles list */}
                    <div className="card-elevated p-3 lg:p-4">
                        <div className="relative mb-3">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Rol ara…"
                                className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                                data-testid="roles-search"
                            />
                            {search && (
                                <button onClick={() => setSearch("")} className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                        <div className="space-y-1 max-h-[560px] overflow-y-auto pr-1">
                            {filtered.map((r) => (
                                <button
                                    key={r.name}
                                    onClick={() => selectRole(r)}
                                    className={`w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left transition-all ${
                                        activeRole?.name === r.name
                                            ? "bg-[hsl(var(--brand))]/10 border border-[hsl(var(--brand))]/30"
                                            : "hover:bg-muted border border-transparent"
                                    }`}
                                    data-testid={`role-item-${r.name}`}
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[13px] font-semibold truncate">{r.display_name}</span>
                                            {r.is_system && <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[9.5px] font-semibold uppercase text-muted-foreground">Sistem</span>}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                                            {r.permission_count} yetki · {r.user_count} kullanıcı
                                        </div>
                                    </div>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Permission matrix */}
                    <div className="card-elevated p-5">
                        {activeRole ? (
                            <>
                                <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h2 className="text-[18px] font-bold tracking-tight">{activeRole.display_name}</h2>
                                            {activeRole.is_system && <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">Sistem Rolü</span>}
                                        </div>
                                        {activeRole.description && <p className="text-[12.5px] text-muted-foreground mt-0.5">{activeRole.description}</p>}
                                        <p className="text-[11.5px] text-muted-foreground mt-1">
                                            <span className="font-semibold text-foreground">{permSet.size}</span> / {totalPerms} yetki aktif
                                        </p>
                                    </div>
                                    {canEdit && (
                                        <button onClick={saveRole} disabled={saving} className="btn-brand h-10" data-testid="role-save">
                                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                            {tr.common.save}
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
                                    {groups.map((g) => {
                                        const groupCodes = g.permissions.map((p) => p.code);
                                        const allSelected = groupCodes.every((c) => permSet.has(c));
                                        const someSelected = groupCodes.some((c) => permSet.has(c));
                                        return (
                                            <div key={g.category} className="rounded-xl border border-border bg-background">
                                                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/30">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[13px] font-semibold">{g.category_label}</span>
                                                        <span className="text-[11px] text-muted-foreground">
                                                            {groupCodes.filter((c) => permSet.has(c)).length}/{groupCodes.length}
                                                        </span>
                                                    </div>
                                                    {canEdit && (
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleGroup(g.permissions, allSelected)}
                                                            className="text-[11.5px] font-semibold text-[hsl(var(--brand))] hover:underline"
                                                        >
                                                            {allSelected ? tr.roles.clearAll : tr.roles.selectAll}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 p-3">
                                                    {g.permissions.map((p) => (
                                                        <label key={p.code} className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-muted/60 cursor-pointer">
                                                            <div className="min-w-0">
                                                                <div className="text-[12.5px] font-medium truncate">{p.name}</div>
                                                                <div className="text-[10.5px] text-muted-foreground font-mono truncate">{p.code}</div>
                                                            </div>
                                                            <Switch
                                                                checked={permSet.has(p.code)}
                                                                onCheckedChange={() => togglePerm(p.code)}
                                                                disabled={!canEdit}
                                                                data-testid={`perm-${p.code}`}
                                                            />
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="py-16 text-center text-muted-foreground">Bir rol seçin</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
